import { SPECIES_NAMES } from "../data/speciesNames";
import { normalizeSpeciesToken } from "./speciesNameMisread";

/**
 * The key under which consecutive My Pokémon frames are chained for the
 * two-frame CP/HP agreement and the three-frame IV agreement.
 *
 * It used to be the display name as OCR printed it. A nickname with a
 * decoration jitters: "Xerneas (+)" came back as "Xerneas (t)", "Xerneas +)",
 * "Xerneas H)" and "Xerneas ()" across one dwell (2026-10-06 Galaxy log), each
 * variant starting the chain again, so the bars were read the same on every
 * frame and never confirmed. The HUD showed ?/?/? the whole time.
 *
 * So: a name that IS the species name, allowing a short tail of OCR debris
 * after it, keys on the species. A real nickname keys on its letters and
 * digits only, lower-cased, so punctuation jitter does not split it either.
 * Whether two same-species Pokémon in a row are the same one is already
 * settled by the CP and max HP agreement, never by this key.
 */
export function myPokemonSightingKey(displayName: string | null, speciesId: number | null): string {
  const token = normalizeSpeciesToken(displayName ?? "");
  if (speciesId != null) {
    const species = normalizeSpeciesToken(SPECIES_NAMES[speciesId] ?? "");
    // The two may differ by up to three characters past their common
    // prefix: OCR debris after the name ("Xerneas H)"), a glyph the reader
    // dropped or mangled (the table's "Nidoran F" printed as "Nidoran" or
    // "Nidoran 9"), or a name the HUD card cut short ("GIGALI" for Gigalith).
    let common = 0;
    while (common < token.length && common < species.length && token[common] === species[common]) common++;
    if (
      token.length === 0 ||
      (species.length > 0 &&
        common >= Math.min(4, species.length) &&
        token.length - common <= 3 &&
        species.length - common <= 3)
    ) {
      return `#${speciesId}`;
    }
  }
  if (token.length === 0) return "?";
  return token.toLowerCase();
}
